import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Pagination,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getInvoicePdf } from '../../../Services/InvoiceService';
import { convertBase64ToBlob } from '../../../../utils/helpers';
import { COLORS } from '../../../../utils/Application_Constants';

const revokeBlobUrls = (items) => {
    items.forEach((item) => {
        if (item.blobUrl) {
            URL.revokeObjectURL(item.blobUrl);
        }
    });
};

const BulkPdfViewerModal = ({ open, onClose, invoices = [] }) => {
    const [pdfItems, setPdfItems] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const invoiceKey = useMemo(
        () => invoices.map((invoice) => invoice.bookingId).join(','),
        [invoices]
    );

    useEffect(() => {
        if (!open || invoices.length === 0) {
            return undefined;
        }

        const cancelTokenSource = axios.CancelToken.source();
        let cancelled = false;
        setIsLoading(true);
        setCurrentIndex(0);
        setPdfItems([]);

        const fetchAll = async () => {
            const results = await Promise.allSettled(
                invoices.map(async (invoice) => {
                    const result = await getInvoicePdf(invoice.bookingId, {
                        cancelToken: cancelTokenSource.token,
                    });
                    const blob = convertBase64ToBlob(result.base64Data, 'application/pdf');
                    const blobUrl = URL.createObjectURL(blob);
                    return {
                        bookingId: invoice.bookingId,
                        invoiceNumber: invoice.invoiceNumber,
                        clientName: invoice.clientName,
                        blobUrl,
                        error: null,
                    };
                })
            );

            if (cancelled) {
                results.forEach((result) => {
                    if (result.status === 'fulfilled' && result.value.blobUrl) {
                        URL.revokeObjectURL(result.value.blobUrl);
                    }
                });
                return;
            }

            const items = results.map((result, index) => {
                const invoice = invoices[index];
                if (result.status === 'fulfilled') {
                    return result.value;
                }
                return {
                    bookingId: invoice.bookingId,
                    invoiceNumber: invoice.invoiceNumber,
                    clientName: invoice.clientName,
                    blobUrl: null,
                    error: 'Failed to load PDF',
                };
            });

            setPdfItems(items);
            setIsLoading(false);

            const firstSuccessIndex = items.findIndex((item) => item.blobUrl);
            if (firstSuccessIndex >= 0) {
                setCurrentIndex(firstSuccessIndex);
            }
        };

        fetchAll();

        return () => {
            cancelled = true;
            cancelTokenSource.cancel();
        };
    }, [open, invoiceKey]);

    useEffect(() => {
        if (!open) {
            setPdfItems((previousItems) => {
                revokeBlobUrls(previousItems);
                return [];
            });
            setCurrentIndex(0);
            setIsLoading(false);
        }
    }, [open]);

    const successIndices = useMemo(
        () =>
            pdfItems
                .map((item, index) => (item.blobUrl ? index : -1))
                .filter((index) => index >= 0),
        [pdfItems]
    );

    const currentSuccessPosition = successIndices.indexOf(currentIndex);
    const successCount = successIndices.length;

    const goToPrevious = useCallback(() => {
        const position = successIndices.indexOf(currentIndex);
        if (position > 0) {
            setCurrentIndex(successIndices[position - 1]);
        }
    }, [currentIndex, successIndices]);

    const goToNext = useCallback(() => {
        const position = successIndices.indexOf(currentIndex);
        if (position >= 0 && position < successIndices.length - 1) {
            setCurrentIndex(successIndices[position + 1]);
        }
    }, [currentIndex, successIndices]);

    const goToPage = useCallback(
        (page) => {
            const index = successIndices[page - 1];
            if (index !== undefined) setCurrentIndex(index);
        },
        [successIndices]
    );

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'ArrowLeft') {
                goToPrevious();
            } else if (event.key === 'ArrowRight') {
                goToNext();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, goToPrevious, goToNext]);

    const currentItem = pdfItems[currentIndex];
    const failedCount = pdfItems.filter((item) => item.error).length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            slotProps={{
                paper: { sx: { display: 'flex', flexDirection: 'column', height: '100%' } },
            }}
        >
            <DialogTitle
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, pt: 2.5, pb: 1.5 }}
            >
                Invoice PDF(s)
                <IconButton onClick={onClose} aria-label="Close PDF viewer">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    px: 2,
                    pb: 2,
                    pt: 0,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {/* eslint-disable-next-line no-nested-ternary */}
                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                        }}
                    >
                        <CircularProgress size={40} />
                    </Box>
                ) : successCount === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                        }}
                    >
                        <Typography color="text.secondary">
                            No PDFs could be loaded. Please try again.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
                        <Box
                            sx={{
                                flex: 1,
                                border: `1px solid ${COLORS.LT_MIDNIGHT10}`,
                                borderRadius: 1,
                                overflow: 'hidden',
                                minWidth: 0,
                                minHeight: 0,
                                display: 'flex',
                            }}
                        >
                            {currentItem?.blobUrl ? (
                                <iframe
                                    title={`Invoice ${currentItem.invoiceNumber}`}
                                    src={currentItem.blobUrl}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 'none', display: 'block', flex: 1 }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flex: 1,
                                    }}
                                >
                                    <Typography color="text.secondary">
                                        {currentItem?.error || 'PDF unavailable'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {currentItem && (
                            <Typography sx={{ textAlign: 'center', color: COLORS.LT_MIDNIGHT }}>
                                {currentItem.clientName} — Invoice #{currentItem.invoiceNumber}
                            </Typography>
                        )}

                        {failedCount > 0 && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: COLORS.LT_PEPPER,
                                    textAlign: 'center',
                                    display: 'block',
                                }}
                            >
                                {failedCount} of {pdfItems.length} PDF
                                {pdfItems.length === 1 ? '' : 's'} failed to load
                            </Typography>
                        )}

                        {successCount > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                                <Pagination
                                    color="primary"
                                    variant="outlined"
                                    shape="rounded"
                                    count={successCount}
                                    page={currentSuccessPosition + 1}
                                    onChange={(_, page) => goToPage(page)}
                                />
                            </Box>
                        )}
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default BulkPdfViewerModal;
